import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASE_URL } from "../../utils/constants";
import { getDefaultHeaders } from "./getDefaultHeaders";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/dist/query/react";
import { getCookie } from "../../utils/token";
import { IResponse } from "../types";
import { logoutSuccess, updateToken } from "../actions/auth";
import { IAuthResponse } from "../types/auth.types";
import { loadAppConfig, resolveApiBaseUrl } from "../../utils/config";
import {
  showFeatureRestrictionPopup,
  showFeatureQuotaRestrictionPopup,
  showResourceRestrictionPopup,
} from "../slices/accessControl.slice";
import { userApi } from "./user.api";
import { aiCreditsRestrictionAction, getCreditBalanceExceeded } from "./creditBalance";

export let BASE_URL_DYNAMIC = BASE_URL;

// Ensure BASE_URL is derived from shared config before initializing the API
try {
  const cfg = await loadAppConfig();
  BASE_URL_DYNAMIC = resolveApiBaseUrl(cfg) || BASE_URL;
} catch (error) {
  console.error("Error loading app config:", error);
}

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL_DYNAMIC,
  // Send the bpreven.auth HttpOnly cookie on cross-origin API calls and
  // honor Set-Cookie from the login response. Required for FileProxy media
  // access via the cookie auth scheme. Safe: CORS allow-list is explicit
  // (no wildcard) and the cookie scheme is only accepted on the read-only
  // FileProxyController.
  credentials: "include",
  prepareHeaders: (headers, endpoints) => {
    const accessToken: string | undefined = getCookie("accessToken");
    return getDefaultHeaders(headers, endpoints, accessToken);
  },
});

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // const refreshToken = (api.getState() as RootState).user.refreshToken;
  const refreshToken = getCookie("refreshToken");
  let result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 429) {
    const creditBalanceBody = getCreditBalanceExceeded(
      result.error.status,
      result.error.data
    );
    if (creditBalanceBody) {
      api.dispatch(aiCreditsRestrictionAction(creditBalanceBody));
      return { data: null };
    }

    const body = result.error.data as {
      error?: string;
      feature?: string;
      scope?: string | null;
      limit?: number;
      used?: number;
      resetsAt?: string | null;
    };
    if (body?.error === "feature_usage_quota_exceeded") {
      api.dispatch(
        showFeatureQuotaRestrictionPopup({
          feature: body.feature ?? "",
          scope: body.scope ?? null,
          limit: body.limit ?? 0,
          used: body.used ?? 0,
          resetsAt: body.resetsAt ?? null,
        })
      );
      return { data: null };
    } else if (body?.error === "ai_token_quota_exceeded") {
      api.dispatch(
        showFeatureQuotaRestrictionPopup({
          feature: "ai_tokens",
          scope: body.scope ?? null,
          limit: body.limit ?? 0,
          used: body.used ?? 0,
          resetsAt: body.resetsAt ?? null,
        })
      );
      return { data: null };
    }
  }

  if (result.error?.status === 403) {
    const errorCode = (result.error.data as { error?: string })?.error;
    if (errorCode === "feature_not_in_plan") {
      api.dispatch(showFeatureRestrictionPopup(errorCode));
      api.dispatch(
        userApi.endpoints.getUserEntitlements.initiate(
          undefined,
          { forceRefetch: true }
        )
      );
      return { data: null };
    } else if (errorCode === "resource_quota_exceeded") {
      api.dispatch(showResourceRestrictionPopup(errorCode));
      return { data: null };
    }
  }

  if (
    refreshToken &&
    result.error &&
    (result.error.status === 401 ||
      (result.error.status === 403 &&
        (result.error.data as IResponse).message === "jwt expired"))
  ) {
    const refreshResult = await baseQuery(
      {
        url: `/token`,
        method: "POST",
        body: {
          token: refreshToken,
        },
      },
      api,
      extraOptions
    );
    if (refreshResult.data) {
      api.dispatch(updateToken(refreshResult.data as IAuthResponse));
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logoutSuccess());
    }
  }
  return result;
};
